import React, { useEffect, useState } from "react";
import { TextField, MenuItem, InputAdornment, IconButton, Tooltip } from "@mui/material";
import useAxios from "../../helpers/Hooks/useAxios";
import { URLS } from "../../helpers/URLS";
import { toast } from "react-toastify";
import NoData from "../../loaders/NoData";
import SkeletonLoader from "../../loaders/Skeleton";
import SearchIcon from "@mui/icons-material/Search";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import moment from "moment";
import PersonAvatar from "../../helpers/PersonAvatar";
import { matchesSearch } from "../../helpers/search";
import { STATUS, scoreTone } from "./helpers";
import "./Performance.scss";

const SEARCH_FIELDS = ["employeeName", "employeeCode", "designation", "managerName", "templateName"];

const Reports = () => {

    const { fetchData } = useAxios();

    const [loading, setLoading] = useState(true);
    const [cycles, setCycles] = useState([]);
    const [cycle, setCycle] = useState("");
    const [data, setData] = useState(null);
    const [search, setSearch] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        const start = async () => {
            const resp = await fetchData({ url: URLS.reviewCycles, method: "get" });
            const list = (resp?.data || []).filter((c) => c.status !== "draft");
            setCycles(list);
            setCycle(list[0]?._id || "");
            if (!list.length) setLoading(false);
        };
        start();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const load = async (quiet) => {
        if (!quiet) setLoading(true);
        const resp = await fetchData({
            url: `${URLS.reviewReport}?cycle=${cycle}`, method: "get",
        });
        setLoading(false);
        if (resp?.success === false) {
            toast.error(resp?.message || "Could not load the report");
            return;
        }
        setData(resp.data);
    };

    useEffect(() => {
        if (!cycle) return;
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cycle]);

    /* HR can nudge anyone who has not started; it goes to the address on
       their employee record. */
    const remind = async (row) => {
        if (busy) return;
        setBusy(true);
        const resp = await fetchData({
            url: `${URLS.reviewRemind}/${row._id}`, method: "post",
        });
        setBusy(false);

        if (resp?.success === false) {
            toast.error(resp?.message || "Could not send the reminder");
            return;
        }
        toast.success(resp?.message || "Reminder sent");
        load(true);
    };

    if (loading) return <SkeletonLoader />;

    if (!cycles.length) {
        return <NoData
            title="No cycles to report on"
            message="Launch a review cycle and the results will be summarised here." />;
    }

    const summary = data?.summary;
    const rows = (data?.rows || []).filter((r) => matchesSearch(r, search, SEARCH_FIELDS));

    return (
        <div className="perf-page">

            <div className="wh-toolbar">
                <TextField select size="small" label="Cycle" value={cycle}
                    className="perf-cycle-picker"
                    onChange={(e) => setCycle(e.target.value)}>
                    {cycles.map((c) => <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>)}
                </TextField>

                <div className="wh-toolbar-controls wh-toolbar-controls--end">
                    <TextField
                        placeholder="Search name, code or manager…"
                        size="small"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="wh-search-field"
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                            endAdornment: search
                                ? <InputAdornment position="end">
                                    <IconButton size="small" className="wh-search-clear"
                                        aria-label="Clear search" onClick={() => setSearch("")}>
                                        <CloseRoundedIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                                : null,
                        }}
                    />
                </div>
            </div>

            {summary && (
                <>
                    <div className="perf-stats">
                        <div className="perf-stat">
                            <div>
                                <span className="perf-stat-label">Completed</span>
                                <b>{summary.completed}</b>
                                <em>of {summary.total} reviews</em>
                            </div>
                        </div>
                        <div className="perf-stat">
                            <div>
                                <span className="perf-stat-label">Awaiting manager</span>
                                <b>{summary.awaitingManager}</b>
                                <em>self assessment done</em>
                            </div>
                        </div>
                        <div className="perf-stat">
                            <div>
                                <span className="perf-stat-label">Not started</span>
                                <b>{summary.pending}</b>
                                <em>no self assessment yet</em>
                            </div>
                        </div>
                        <div className="perf-stat">
                            <div>
                                <span className="perf-stat-label">Average rating</span>
                                <b>{summary.averageManager || "--"}</b>
                                <em>self average {summary.averageSelf || "--"}</em>
                            </div>
                        </div>
                    </div>

                    {/* the spread of finished reviews across the scale */}
                    <div className="wh-panel">
                        <h6 className="wh-section-title" style={{ marginTop: 0 }}>Rating spread</h6>
                        <div className="perf-bands">
                            {summary.bands.map((band) => {
                                const share = summary.completed
                                    ? Math.round((band.count / summary.completed) * 100) : 0;
                                return (
                                    <div className="perf-band" key={band.label}>
                                        <span className="perf-band-label">{band.label}</span>
                                        <span className="perf-band-track">
                                            <span className="perf-band-fill" style={{ width: `${share}%` }} />
                                        </span>
                                        <span className="perf-band-count">{band.count}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </>
            )}

            <div className="table-responsive wh-table-shell">
                <table className="table">
                    <thead>
                        <tr>
                            <th scope="col">Employee</th>
                            <th scope="col" className="wh-col-shrink">Reviewed against</th>
                            <th scope="col">Manager</th>
                            <th scope="col" className="wh-col-shrink">Status</th>
                            <th scope="col" className="wh-col-shrink">Self</th>
                            <th scope="col" className="wh-col-shrink">Manager</th>
                            <th scope="col" className="wh-col-shrink text-center">Remind</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => {
                            const meta = STATUS[row.status] || STATUS.pending;
                            return (
                                <tr key={row._id}>
                                    <td>
                                        <div className="wh-person">
                                            <PersonAvatar name={row.employeeName} pic={row.profilePic} />
                                            <span className="perf-person">
                                                <b>{row.employeeName}</b>
                                                <em>{row.designation || row.employeeCode}</em>
                                            </span>
                                        </div>
                                    </td>
                                    <td className="wh-col-shrink">
                                        {row.templateName
                                            ? <span className="wh-pill wh-pill-quiet">{row.templateName}</span>
                                            : "--"}
                                    </td>
                                    <td>{row.managerName || "--"}</td>
                                    <td className="wh-col-shrink">
                                        <span className={`wh-pill ${meta.tone}`}>{meta.label}</span>
                                    </td>
                                    <td className="wh-col-shrink">
                                        {row.selfScore
                                            ? <span className={`wh-pill ${scoreTone(row.selfScore)}`}>{row.selfScore}</span>
                                            : "--"}
                                    </td>
                                    <td className="wh-col-shrink">
                                        {row.managerScore
                                            ? <span className={`wh-pill ${scoreTone(row.managerScore)}`}>{row.managerScore}</span>
                                            : "--"}
                                    </td>

                                    {/* only someone who has not started can be nudged */}
                                    <td className="wh-col-shrink text-center">
                                        {row.status === "pending"
                                            ? <Tooltip
                                                title={row.remindedAt
                                                    ? `Last reminded ${moment(row.remindedAt).format("DD MMM, h:mm A")}`
                                                    : `Email ${row.employeeName} about their self assessment`}
                                                placement="top" arrow>
                                                <span>
                                                    <IconButton disabled={busy}
                                                        className={row.remindedAt ? "perf-reminded" : ""}
                                                        aria-label={`Remind ${row.employeeName}`}
                                                        onClick={() => remind(row)}>
                                                        <NotificationsActiveOutlinedIcon />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            : <span className="perf-dash">--</span>}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default Reports;
